import { useEffect, useMemo, useRef, useState } from 'react';
import { Mic, Type, Upload, CheckCircle2, ExternalLink, Save, Send, Loader2, AlertTriangle, Globe, Eye } from 'lucide-react';
import { AudioRecorder } from './AudioRecorder';
import { InterviewChat } from './InterviewChat';
import { DraftReview } from './DraftReview';
import { FileUploader } from './FileUploader';
import { HubspotPreviewModal } from './HubspotPreviewModal';
import { DealPicker } from './DealPicker';
import { MarketObservations } from './MarketObservations';
import { MarketDataStep } from './MarketDataStep';
import { visitReportsApi } from '../../services/visitReportsApi';
import { useToast } from '../ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { sanitizePastedText } from '../../utils/sanitizeText';
import type {
    VisitReportDraft,
    InterviewMessage,
    VisitReportRecord,
    HubspotPreview,
    ActivityTypeOption,
    MarketObservation,
} from '../../types/visitReport';

interface VisitReportWizardProps {
    hubspotCompanyId: string;
    companyName?: string | null;
    customerId?: string;
    companyCode?: string;
    /** When set, wizard loads the existing draft and jumps straight to the interview/review step. */
    initialDraftId?: string;
    /** Called after a successful HubSpot sync. */
    onSynced?: (engagementUrl: string) => void;
}

type Step = 'input' | 'interview' | 'review' | 'synced';

const LANGUAGES = [
    { code: 'auto', label: 'Auto-detect' },
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'it', label: 'Italiano' },
    { code: 'fr', label: 'Francais' },
    { code: 'es', label: 'Espanol' },
    { code: 'tr', label: 'Turkce' },
    { code: 'pt', label: 'Portugues' },
];

export function VisitReportWizard({
    hubspotCompanyId,
    companyName,
    customerId,
    companyCode,
    initialDraftId,
    onSynced,
}: VisitReportWizardProps) {
    const { user } = useAuth();
    const { toast } = useToast();
    const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

    const [step, setStep] = useState<Step>('input');
    const [inputMode, setInputMode] = useState<'audio' | 'text' | 'upload'>('audio');
    const [transcript, setTranscript] = useState<string>('');
    const [language, setLanguage] = useState<string>('auto');
    const [uploadFileName, setUploadFileName] = useState<string | null>(null);
    const [isExtractingUpload, setIsExtractingUpload] = useState(false);

    const [draft, setDraft] = useState<VisitReportDraft>({
        visitDate: today,
        dolderAttendees: user?.email ? [formatRepName(user.email)] : [],
    });
    const [draftId, setDraftId] = useState<string | null>(null);
    const [conversation, setConversation] = useState<InterviewMessage[]>([]);
    const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);

    // Refs that mirror the latest values so async save callbacks see fresh data
    // (closure-captured state can be stale during overlapping autosaves).
    const draftIdRef = useRef<string | null>(null);
    useEffect(() => { draftIdRef.current = draftId; }, [draftId]);

    // The attached deal lives in the HubspotDealId COLUMN, not in the draft JSON — which is what
    // saves it from the trap that forced activityType into the request body: an interview turn
    // replaces the whole draft and would swallow a value stored in there.
    //
    // It is mirrored into a ref for the same reason draftId is: `autoSave` runs asynchronously
    // and a `setState` from the picker has not landed by the time the save reads it. The ref is
    // set synchronously in the change handler, so the save always writes the rep's actual choice.
    const [dealId, setDealId] = useState<string | null>(null);
    const dealIdRef = useRef<string | null>(null);
    useEffect(() => { dealIdRef.current = dealId; }, [dealId]);
    const inflightSaveRef = useRef<Promise<unknown> | null>(null);
    /** Debounce handle for the market-data section — see `handleMarketObservationsChange`. */
    const marketSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => { if (marketSaveRef.current) clearTimeout(marketSaveRef.current); }, []);

    const [isTranscribing, setIsTranscribing] = useState(false);
    const [isSendingInterview, setIsSendingInterview] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [interviewComplete, setInterviewComplete] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [syncResult, setSyncResult] = useState<{ url: string | null; reportId: string } | null>(null);

    // ---------- HubSpot Activity Type + preview ----------
    // The list is read live from the write portal; there is deliberately no hard-coded fallback
    // set, because PROD's seven values are configuration that Marketing can change and TEST has
    // none at all. Until it arrives the select shows just the default.
    const [activityTypes, setActivityTypes] = useState<ActivityTypeOption[]>([]);
    const [activityType, setActivityType] = useState<string>('Standard Visit');
    const [activityTypesError, setActivityTypesError] = useState<string | null>(null);
    const [preview, setPreview] = useState<HubspotPreview | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    // Load existing draft if an ID is provided
    useEffect(() => {
        if (!initialDraftId) return;
        let cancelled = false;
        visitReportsApi.getDraft(initialDraftId)
            .then((record: VisitReportRecord) => {
                if (cancelled) return;
                setDraftId(record.id);
                draftIdRef.current = record.id;
                setDraft(record.draft || {});
                setDealId(record.hubspotDealId ?? null);
                dealIdRef.current = record.hubspotDealId ?? null;
                if (record.draft?.activityType) setActivityType(record.draft.activityType);
                setTranscript(record.rawTranscript || '');
                setLanguage(record.language || 'auto');
                const restoredHistory = Array.isArray(record.conversationHistory) ? record.conversationHistory : [];
                setConversation(restoredHistory);
                // If the last assistant turn was a question, surface it again so the rep can answer.
                const lastAssistant = [...restoredHistory].reverse().find(m => m.role === 'assistant');
                if (lastAssistant && record.wizardStep === 'interview') {
                    setPendingQuestion(lastAssistant.content);
                }
                // A synced report goes to the synced screen, whatever `wizardStep` says. That
                // column holds the last step the rep was ON, which is 'review' — the sync
                // itself never autosaves. Trusting it would drop a reopened synced report back
                // into a review screen offering to sync a report that is already in the CRM,
                // and would put the market-data step (P5) out of reach for good: after a
                // reload, this is the ONLY way back to it.
                if (record.status === 'synced') {
                    setStep('synced');
                    setSyncResult({ url: record.hubspotUrl ?? null, reportId: record.id });
                } else if (record.wizardStep) {
                    setStep(record.wizardStep);
                } else {
                    const hasData = Object.keys(record.draft || {}).length > 2;
                    setStep(hasData ? 'review' : 'interview');
                }
            })
            .catch(err => {
                if (!cancelled) setError(err.message || 'Failed to load draft');
            });
        return () => { cancelled = true; };
    }, [initialDraftId]);

    // ---------- Step 1: input ----------

    const handleAudioReady = async (audioBase64: string, mimeType: string, _durationSec: number) => {
        setError(null);
        setIsTranscribing(true);
        try {
            const languageHint = language === 'auto' ? undefined : language;
            const result = await visitReportsApi.transcribe({
                audioBase64,
                mimeType,
                languageHint,
                vocabularyPrompt: companyName ? `Customer: ${companyName}` : undefined,
            });
            setTranscript(result.transcript);
            if (result.detectedLanguage && language === 'auto') {
                setLanguage(result.detectedLanguage);
            }
            // Immediately start the AI interview
            await runInterview({
                transcript: result.transcript,
                language: result.detectedLanguage || (language !== 'auto' ? language : undefined),
            });
        } catch (err: any) {
            setError(err?.message || 'Failed to transcribe audio');
        } finally {
            setIsTranscribing(false);
        }
    };

    const handleTextSubmit = async () => {
        // Safety net — sanitize even manually-typed input in case the user pasted
        // somewhere in the middle without triggering onPaste (e.g. drag-drop).
        const txt = sanitizePastedText(transcript);
        if (txt !== transcript) setTranscript(txt);
        if (!txt) {
            setError('Please enter some text first.');
            return;
        }
        setError(null);
        await runInterview({ transcript: txt, language: language !== 'auto' ? language : undefined });
    };

    const handleFileReady = async (fileBase64: string, mimeType: string, fileName: string) => {
        setError(null);
        setUploadFileName(fileName);
        setIsExtractingUpload(true);
        try {
            const languageHint = language === 'auto' ? undefined : language;
            const result = await visitReportsApi.extractUpload({
                fileBase64,
                mimeType,
                fileName,
                languageHint,
                companyName: companyName ?? undefined,
            });
            const cleaned = sanitizePastedText(result.extractedText);
            setTranscript(cleaned);
        } catch (err: any) {
            setError(err?.message || 'Failed to extract text from file');
            setUploadFileName(null);
        } finally {
            setIsExtractingUpload(false);
        }
    };

    const handleClearUpload = () => {
        setUploadFileName(null);
        setTranscript('');
        setError(null);
    };

    // ---------- Step 2: interview ----------

    const runInterview = async (opts: {
        transcript?: string;
        userMessage?: string;
        language?: string;
    }) => {
        setIsSendingInterview(true);
        setStep('interview');
        try {
            const nextHistory: InterviewMessage[] = [...conversation];
            // On the first turn, seed the chat with the transcript so the user sees
            // what was captured. Backend still sends the transcript as a structured
            // field for Claude — this is purely for UX.
            const isFirstTurn = nextHistory.length === 0;
            if (isFirstTurn && opts.transcript) {
                nextHistory.push({ role: 'user', content: opts.transcript });
            }
            if (opts.userMessage) nextHistory.push({ role: 'user', content: opts.userMessage });

            const resp = await visitReportsApi.interview({
                hubspotCompanyId,
                customerId,
                companyCode,
                transcript: opts.transcript ?? transcript,
                currentDraft: draft,
                conversationHistory: nextHistory,
                language: opts.language ?? (language !== 'auto' ? language : undefined),
                salesRepName: user?.email ? formatRepName(user.email) : undefined,
            });

            const nextDraft = resp.updatedDraft || draft;
            setDraft(nextDraft);
            setInterviewComplete(resp.isComplete);

            if (resp.assistantQuestion) {
                nextHistory.push({ role: 'assistant', content: resp.assistantQuestion });
                setPendingQuestion(resp.assistantQuestion);
            } else if (!resp.isComplete) {
                // Claude returned neither a question nor completion — fall back to a
                // generic prompt so the rep isn't stuck with an empty chat. They can
                // always use "I'm done — skip further questions" to jump to review.
                const fallback = 'Anything else worth capturing — products discussed, next steps, follow-up date?';
                nextHistory.push({ role: 'assistant', content: fallback });
                setPendingQuestion(fallback);
            } else {
                setPendingQuestion(null);
            }
            setConversation(nextHistory);

            // Persist progress after every turn so a tab switch / refresh can resume
            // mid-interview, not just on completion.
            const newStep: Step = resp.isComplete ? 'review' : 'interview';
            await autoSave(nextDraft, {
                conversation: nextHistory,
                step: newStep,
                transcript: opts.transcript ?? transcript,
            });

            if (resp.isComplete) setStep('review');
        } catch (err: any) {
            setError(err?.message || 'AI interview failed');
        } finally {
            setIsSendingInterview(false);
        }
    };

    const handleAnswerQuestion = async (answer: string) => {
        setPendingQuestion(null);
        await runInterview({ userMessage: answer, language: language !== 'auto' ? language : undefined });
    };

    const handleFinishEarly = async () => {
        setInterviewComplete(true);
        await autoSave(draft, { conversation, step: 'review' });
        setStep('review');
    };

    // The option list costs a HubSpot call, so it is fetched when the rep actually reaches the
    // step that offers the choice — not on every page load.
    useEffect(() => {
        if (step !== 'review' || activityTypes.length > 0) return;
        let cancelled = false;
        visitReportsApi.activityTypes()
            .then(res => {
                if (cancelled) return;
                setActivityTypes(res.options);
                setActivityTypesError(res.error);
                // Only adopt the server default while the rep has not chosen anything yet.
                setActivityType(prev => (res.options.some(o => o.value === prev) ? prev : res.defaultValue));
            })
            .catch(err => { if (!cancelled) setActivityTypesError(err?.message || 'Could not load Activity Types'); });
        return () => { cancelled = true; };
    }, [step, activityTypes.length]);

    // ---------- Step 3: review ----------

    const autoSave = async (
        nextDraft: VisitReportDraft,
        overrides?: { conversation?: InterviewMessage[]; step?: Step; transcript?: string },
    ) => {
        // Serialize overlapping saves so the second one sees the draftId from the first.
        const previous = inflightSaveRef.current;
        if (previous) {
            try { await previous; } catch { /* swallow — toast already raised */ }
        }
        const promise = (async () => {
            setIsSaving(true);
            try {
                const res = await visitReportsApi.saveDraft({
                    id: draftIdRef.current ?? undefined,
                    hubspotCompanyId,
                    customerId,
                    companyCode,
                    // Sent on EVERY save, not only from the review step. save-draft writes
                    // `[HubspotDealId] = @hubspotDealId` unconditionally, so omitting it would
                    // silently detach a deal the rep picked earlier.
                    hubspotDealId: dealIdRef.current ?? undefined,
                    visitDate: nextDraft.visitDate ?? today,
                    language: language !== 'auto' ? language : undefined,
                    rawTranscript: overrides?.transcript ?? transcript,
                    // Kept in the draft so the choice survives a reload. The value that actually
                    // governs a write travels in the sync request body — an interview turn
                    // replaces the whole draft and would swallow this field.
                    draft: { ...nextDraft, activityType },
                    conversationHistory: overrides?.conversation ?? conversation,
                    wizardStep: overrides?.step ?? step,
                });
                if (!draftIdRef.current) {
                    draftIdRef.current = res.id;
                    setDraftId(res.id);
                }
            } catch (err: any) {
                toast(err?.message || 'Failed to save draft', 'error');
            } finally {
                setIsSaving(false);
            }
        })();
        inflightSaveRef.current = promise;
        try {
            await promise;
        } finally {
            if (inflightSaveRef.current === promise) inflightSaveRef.current = null;
        }
    };

    // Debounced autosave while typing/reviewing the raw input. Captures the rep's
    // notes after 3s of idle so a tab switch / refresh during step 1 doesn't lose them.
    useEffect(() => {
        if (step !== 'input') return;
        if (transcript.trim().length < 20) return;
        const handle = setTimeout(() => {
            autoSave(draft, { step: 'input', transcript });
        }, 3000);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transcript, step]);

    /**
     * Persist the attachment immediately rather than on the next save.
     *
     * The ref is set before the save so the request carries the new choice — `setDealId` alone
     * would not have landed yet. Saving right away also means the preview, which reads the draft
     * from the database and not from this component, names the deal the rep just picked.
     */
    const handleDealChange = async (nextDealId: string | null, nextDealName: string | null) => {
        dealIdRef.current = nextDealId;
        setDealId(nextDealId);
        await autoSave(draft, { conversation, step: 'review' });
        toast(nextDealId ? `Attached to ${nextDealName}` : 'Deal detached', 'success');
    };

    /**
     * Market observations live in the draft JSON, so they ride the ordinary autosave — but the
     * save has to see the NEW array, not the one captured in this closure. `setDraft` has not
     * landed when `autoSave` reads its argument, which is the same reason the deal id needs a
     * ref; here the next draft is simply passed in explicitly.
     *
     * Debounced, because a rep typing a price would otherwise fire a save per keystroke and
     * every one of them re-validates the alternatives against the product master.
     */
    const handleMarketObservationsChange = (next: MarketObservation[]) => {
        const nextDraft = { ...draft, marketObservations: next };
        setDraft(nextDraft);
        if (marketSaveRef.current) clearTimeout(marketSaveRef.current);
        marketSaveRef.current = setTimeout(() => {
            void autoSave(nextDraft, { conversation, step: 'review' });
        }, 1200);
    };

    const handleManualSave = async () => {
        await autoSave(draft, { conversation, step });
        toast('Draft saved', 'success');
    };

    /** Both the preview and the sync work off a saved draft id, not off component state. */
    const ensureDraftId = async (): Promise<string | null> => {
        if (!draftIdRef.current) {
            await autoSave(draft, { conversation, step });
        }
        return draftIdRef.current;
    };

    const handlePreview = async () => {
        const id = await ensureDraftId();
        if (!id) {
            toast('Could not save draft before preview', 'error');
            return;
        }
        setIsPreviewOpen(true);
        setIsPreviewLoading(true);
        setPreviewError(null);
        setPreview(null);
        try {
            const result = await visitReportsApi.previewHubspot(id, activityType);
            setPreview(result);
        } catch (err: any) {
            setPreviewError(err?.message || 'Could not build the preview');
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const handleSync = async () => {
        const idForSync = await ensureDraftId();
        if (!idForSync) {
            toast('Could not save draft before sync', 'error');
            return;
        }
        setIsSyncing(true);
        try {
            const result = await visitReportsApi.syncHubspot(idForSync, activityType);
            setSyncResult({ url: result.hubspotUrl, reportId: idForSync });
            setIsPreviewOpen(false);
            setStep('synced');
            toast('Synced to HubSpot', 'success');
            onSynced?.(result.hubspotUrl);
        } catch (err: any) {
            toast(err?.message || 'HubSpot sync failed', 'error');
        } finally {
            setIsSyncing(false);
        }
    };

    // ---------- Render ----------

    return (
        <div className="space-y-4 min-w-0">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2 min-w-0">
                <h2 className="text-lg font-semibold text-gray-800 break-words min-w-0">
                    Visit Report{companyName ? ` · ${companyName}` : ''}
                </h2>
                <div className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-sm">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <select
                        value={language}
                        onChange={e => setLanguage(e.target.value)}
                        className="bg-transparent focus:outline-none cursor-pointer pr-1"
                    >
                        {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                    </select>
                </div>
            </div>

            {/* Step progress */}
            <StepIndicator step={step} />

            {error && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {/* Step 1: input */}
            {step === 'input' && (
                <div className="space-y-4">
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setInputMode('audio')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition ${
                                inputMode === 'audio'
                                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                        >
                            <Mic className="w-5 h-5" /> Speak
                        </button>
                        <button
                            type="button"
                            onClick={() => setInputMode('text')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition ${
                                inputMode === 'text'
                                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                        >
                            <Type className="w-5 h-5" /> Type
                        </button>
                        <button
                            type="button"
                            onClick={() => setInputMode('upload')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition ${
                                inputMode === 'upload'
                                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                        >
                            <Upload className="w-5 h-5" /> Upload
                        </button>
                    </div>

                    {inputMode === 'audio' && (
                        <div className="py-6 flex flex-col items-center gap-4">
                            <p className="text-sm text-gray-600 text-center max-w-md">
                                Tap the button and tell the story of your visit — who you met, what you discussed, next steps.
                                The AI will structure it into a report and ask for anything missing.
                            </p>
                            <AudioRecorder onAudioReady={handleAudioReady} isUploading={isTranscribing} />
                        </div>
                    )}

                    {inputMode === 'text' && (
                        <div className="space-y-3">
                            <textarea
                                value={transcript}
                                onChange={e => setTranscript(e.target.value)}
                                onPaste={e => {
                                    // Sanitize text pasted from rich-text editors (HubSpot,
                                    // Word, Outlook, Notion) — strips HTML, HTML entities,
                                    // Unicode separators, non-breaking spaces, control
                                    // chars. Prevents silent JSON/LLM failures downstream.
                                    const pasted = e.clipboardData.getData('text/plain')
                                        || e.clipboardData.getData('text/html');
                                    if (!pasted) return;
                                    e.preventDefault();
                                    const cleaned = sanitizePastedText(pasted);
                                    const el = e.currentTarget;
                                    const start = el.selectionStart ?? transcript.length;
                                    const end = el.selectionEnd ?? transcript.length;
                                    const next = transcript.slice(0, start) + cleaned + transcript.slice(end);
                                    setTranscript(next);
                                }}
                                placeholder="Write a free-text account of the visit. Who, what, next steps — don't worry about structure; the AI will organize it."
                                rows={8}
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                            />
                            <button
                                type="button"
                                onClick={handleTextSubmit}
                                disabled={!transcript.trim() || isSendingInterview}
                                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 text-sm font-medium"
                            >
                                {isSendingInterview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                Start structuring with AI
                            </button>
                        </div>
                    )}

                    {inputMode === 'upload' && (
                        <div className="space-y-3">
                            {!uploadFileName || isExtractingUpload || !transcript ? (
                                <>
                                    <p className="text-sm text-gray-600">
                                        Upload a photo of handwritten notes, a PDF, or a Word document. We'll extract the text and
                                        let you review it before the AI structures it into a report.
                                    </p>
                                    <FileUploader onFileReady={handleFileReady} isProcessing={isExtractingUpload} />
                                </>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <p className="text-sm text-gray-700">
                                            Extracted from <span className="font-medium">{uploadFileName}</span>. Review and fix any
                                            errors before we structure it.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={handleClearUpload}
                                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                                        >
                                            Upload a different file
                                        </button>
                                    </div>
                                    <textarea
                                        value={transcript}
                                        onChange={e => setTranscript(e.target.value)}
                                        rows={10}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleTextSubmit}
                                        disabled={!transcript.trim() || isSendingInterview}
                                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 text-sm font-medium"
                                    >
                                        {isSendingInterview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                        Start structuring with AI
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Step 2: interview */}
            {step === 'interview' && (
                <InterviewChat
                    conversation={conversation}
                    pendingQuestion={pendingQuestion}
                    isSending={isSendingInterview}
                    isComplete={interviewComplete}
                    onSendAnswer={handleAnswerQuestion}
                    onFinishEarly={handleFinishEarly}
                />
            )}

            {/* Step 3: review */}
            {step === 'review' && (
                <div className="space-y-4">
                    <DraftReview draft={draft} onChange={(patch) => setDraft({ ...draft, ...patch })} companyName={companyName} companyCode={companyCode} />

                    {/* Market data (P4). Captured into the draft, written to HubSpot by nothing on
                        this screen — phase 5 adds the separate, acknowledged step that creates the
                        records after the report has synced (D4). */}
                    <MarketObservations
                        observations={draft.marketObservations ?? []}
                        onChange={handleMarketObservationsChange}
                        companyCode={companyCode}
                        disabled={isSyncing}
                    />

                    {/* Optional by design. D8: the picker attaches to an existing deal and never
                        creates one — a new deal carries its own pipeline and stage logic and does
                        not belong in a report assistant. */}
                    <DealPicker
                        hubspotCompanyId={hubspotCompanyId}
                        value={dealId}
                        onChange={handleDealChange}
                        disabled={isSaving || isSyncing}
                    />

                    <div className="sticky bottom-0 bg-white border-t border-gray-200 pt-3 flex flex-wrap items-center gap-2 justify-between">
                        <button
                            type="button"
                            onClick={handleManualSave}
                            disabled={isSaving}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                        >
                            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Save draft
                        </button>

                        <div className="flex flex-wrap items-center gap-2">
                            {/* The values come from the write portal at run time. A hard-coded list
                                would be a copy of HubSpot configuration that nobody updates, and the
                                TEST portal has no values at all. */}
                            <label className="flex items-center gap-1.5 text-sm text-gray-600">
                                <span className="hidden sm:inline">Activity type</span>
                                <select
                                    value={activityType}
                                    onChange={e => setActivityType(e.target.value)}
                                    className="px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    title={activityTypesError || 'How this visit is classified in HubSpot'}
                                >
                                    {activityTypes.length === 0 && <option value={activityType}>{activityType}</option>}
                                    {activityTypes.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </label>

                            <button
                                type="button"
                                onClick={handlePreview}
                                disabled={isSaving || isSyncing}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                            >
                                <Eye className="w-4 h-4" />
                                Preview
                            </button>
                            <button
                                type="button"
                                onClick={handleSync}
                                disabled={isSyncing || isSaving}
                                className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 text-sm font-semibold"
                            >
                                {isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                Sync to HubSpot
                            </button>
                        </div>
                    </div>

                    {isPreviewOpen && (
                        <HubspotPreviewModal
                            preview={preview}
                            isLoading={isPreviewLoading}
                            error={previewError}
                            isSyncing={isSyncing}
                            onClose={() => setIsPreviewOpen(false)}
                            onConfirm={handleSync}
                        />
                    )}
                </div>
            )}

            {/* Step 4: synced — and, separately, the market data (P5, D4).

                The market-data step is deliberately NOT part of the sync above. The sync is
                three HubSpot calls that already roll back on the second or third; N more writes
                inside the same button press would let a rejected market data record throw away
                a synced report. It lives here instead: the report is safely in the CRM, and each
                observation is confirmed on its own. Closing the page at this point loses
                nothing — the step reads its state from the server and can be run again. */}
            {step === 'synced' && syncResult && (
                <div className="flex flex-col items-center gap-4 py-10 text-center">
                    <CheckCircle2 className="w-14 h-14 text-green-600" />
                    <h3 className="text-xl font-semibold text-gray-800">Synced to HubSpot</h3>
                    <p className="text-sm text-gray-600 max-w-md">
                        Your visit report is now attached to the customer in HubSpot as a Meeting engagement.
                    </p>
                    {syncResult.url && (
                        <a
                            href={syncResult.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        >
                            Open in HubSpot <ExternalLink className="w-4 h-4" />
                        </a>
                    )}

                    {(draft.marketObservations?.length ?? 0) > 0 && (
                        <div className="w-full max-w-3xl pt-6 mt-2 border-t border-gray-200">
                            <MarketDataStep visitReportId={syncResult.reportId} companyName={companyName} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function StepIndicator({ step }: { step: Step }) {
    const steps: Array<{ key: Step; label: string; shortLabel: string }> = [
        { key: 'input', label: 'Capture', shortLabel: 'Capture' },
        { key: 'interview', label: 'AI Interview', shortLabel: 'Interview' },
        { key: 'review', label: 'Review', shortLabel: 'Review' },
        { key: 'synced', label: 'Synced', shortLabel: 'Synced' },
    ];
    const idx = steps.findIndex(s => s.key === step);
    return (
        <div className="flex items-center gap-1 text-xs">
            {steps.map((s, i) => {
                const isPast = i < idx;
                const isActive = i === idx;
                return (
                    <div key={s.key} className="flex items-center gap-1 min-w-0">
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-full whitespace-nowrap ${
                            isPast ? 'bg-green-100 text-green-700'
                            : isActive ? 'bg-blue-100 text-blue-700 font-semibold'
                            : 'bg-gray-100 text-gray-400'
                        }`}>
                            <span>{i + 1}</span>
                            {/* Show label on active step always; on past/future only on wider screens */}
                            <span className={isActive ? 'inline' : 'hidden sm:inline'}>
                                <span className="sm:hidden">{s.shortLabel}</span>
                                <span className="hidden sm:inline">{s.label}</span>
                            </span>
                        </div>
                        {i < steps.length - 1 && <div className="w-2 sm:w-4 h-px bg-gray-300 flex-shrink-0" />}
                    </div>
                );
            })}
        </div>
    );
}

function formatRepName(email: string): string {
    const local = email.split('@')[0] || email;
    return local
        .split(/[._-]/)
        .filter(Boolean)
        .map(s => s[0].toUpperCase() + s.slice(1))
        .join(' ');
}
